import React, { useEffect, useMemo, useRef, useState } from "react";
import apiService from "../services/apiService"; // Adjust import path if needed

const emptyChat = () => ({
  id: crypto.randomUUID(),
  title: "New consultation",
  createdAt: Date.now(),
  mode: null,
  messages: [],
});

const MODES = {
  disease: {
    label: "Disease Management",
    icon: "🐔",
    color: "#b45309",
    bg: "#fef3c7",
    border: "#f59e0b",
    system: `You are an expert AI assistant for UK poultry disease management. You help poultry farmers:
- Identify potential diseases from symptoms described
- Match them with certified UK poultry health professionals (vets, specialists)
- Provide evidence-based treatment recommendations aligned with UK veterinary guidelines
- Give medication dosage guidance strictly following UK regulations (always recommend prescription through a vet for prescription medications)
- Advise on biosecurity, isolation, and flock management

Always:
- Ask clarifying questions about symptoms, bird age, species, and flock size
- Recommend consulting a certified poultry vet for diagnosis confirmation
- Note any UK-specific regulatory requirements (e.g., notifiable diseases like Avian Influenza must be reported to APHA)
- Be clear that your guidance is advisory and not a substitute for professional veterinary diagnosis

Format responses clearly with sections where helpful.`,
    starters: [
      "My hens have swollen faces and reduced egg production",
      "I'm seeing respiratory distress in my broilers",
      "Several birds are showing neurological signs",
      "I need advice on biosecurity after a disease outbreak",
    ],
  },
  litter: {
    label: "Litter Reuse & W2E",
    icon: "♻️",
    color: "#065f46",
    bg: "#d1fae5",
    border: "#10b981",
    system: `You are an expert AI assistant for poultry litter management and Waste-to-Energy (W2E) systems for UK poultry farmers. You help with:
- Guidance on repurposing poultry litter into animal feed, fertiliser, or fuel
- Waste-to-Energy systems: anaerobic digestion, combustion, gasification, pyrolysis
- UK regulatory compliance (DEFRA guidelines, Environment Agency permits, NVZ rules)
- Nutrient management planning
- Economic analysis of different litter reuse pathways
- Carbon footprint and sustainability benefits

Always:
- Reference UK-specific regulations (England, Scotland, Wales, Northern Ireland may differ)
- Highlight permit requirements for spreading, composting, or energy generation
- Mention nitrate vulnerable zone (NVZ) restrictions where relevant
- Provide practical cost-benefit considerations

Format responses clearly with sections and practical next steps.`,
    starters: [
      "What are my options for using broiler litter as fertiliser?",
      "Can poultry litter be used as animal feed legally in the UK?",
      "Tell me about anaerobic digestion for poultry waste",
      "What permits do I need to spread litter on my fields?",
    ],
  },
};

export default function PoultryAIChatbot() {
  const [chats, setChats] = useState(() => {
    try {
      const s = localStorage.getItem("poultry:chats:v1");
      return s ? JSON.parse(s) : [emptyChat()];
    } catch {
      return [emptyChat()];
    }
  });

  const [activeChatId, setActiveChatId] = useState(() => chats[0]?.id || null);
  const [search, setSearch] = useState("");
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const activeChatItemRef = useRef(null);
  const textareaRef = useRef(null);

  const activeChat = useMemo(
    () => chats.find((c) => c.id === activeChatId) || chats[0],
    [chats, activeChatId]
  );
  const messages = activeChat?.messages ?? [];
  const currentMode = activeChat?.mode ? MODES[activeChat.mode] : null;

  useEffect(() => {
    localStorage.setItem("poultry:chats:v1", JSON.stringify(chats));
  }, [chats]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, activeChatId]);

  useEffect(() => {
    activeChatItemRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [activeChatId, chats.length]);

  const updateActiveChat = (updater) => {
    setChats((prev) => prev.map((c) => (c.id === activeChatId ? updater(c) : c)));
  };

  const startNewChat = () => {
    const fresh = emptyChat();
    setChats((prev) => [fresh, ...prev]);
    setActiveChatId(fresh.id);
    setInputMessage("");
  };

  const deleteChat = (id) => {
    setChats((prev) => {
      const filtered = prev.filter((c) => c.id !== id);
      const result = filtered.length ? filtered : [emptyChat()];
      setActiveChatId(result[0].id);
      return result;
    });
  };

  const selectMode = (modeKey) => {
    updateActiveChat((c) => ({ ...c, mode: modeKey, title: MODES[modeKey].label + " Consultation" }));
  };

  const handleStarterClick = (text) => {
    setInputMessage(text);
    textareaRef.current?.focus();
  };

  const sendMessage = async (text) => {
    const trimmed = text?.trim() || inputMessage.trim();
    if (!trimmed || isLoading || !activeChat?.mode) return;

    const userMsg = { role: "user", content: trimmed, ts: Date.now() };

    updateActiveChat((c) => ({
      ...c,
      messages: [...c.messages, userMsg],
      title: c.messages.length === 0
        ? trimmed.slice(0, 45) + (trimmed.length > 45 ? "…" : "")
        : c.title,
    }));

    setInputMessage("");
    setIsLoading(true);

    try {
      const modeConfig = MODES[activeChat.mode];
      const history = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));

      // 🔥 Using your backend proxy (chatAi.js)
      const data = await apiService.post("/chat-ai", {
        messages: history,
        max_tokens: 1200,
       model: "gpt-4o-mini",   // Change this if you switch backend to OpenAI
        system: modeConfig.system,
      });

      // Works with your current Anthropic proxy (which returns OpenAI-style format)
      const replyText = data?.choices?.[0]?.message?.content ||
                        data?.content ||
                        "I didn't receive a proper response. Please try again.";

      updateActiveChat((c) => ({
        ...c,
        messages: [...c.messages, { role: "assistant", content: replyText, ts: Date.now() }],
      }));
    } catch (err) {
      console.error("Chat error:", err);
      const errorMsg = err?.response?.data?.error || 
                      err?.message || 
                      "Sorry, an error occurred. Please try again.";
      
      updateActiveChat((c) => ({
        ...c,
        messages: [...c.messages, { role: "assistant", content: errorMsg, ts: Date.now() }],
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const filteredChats = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = [...chats].sort((a, b) => b.createdAt - a.createdAt);
    if (!q) return base;
    return base.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        (c.messages?.slice(-1)?.[0]?.content ?? "").toLowerCase().includes(q)
    );
  }, [chats, search]);

  const formatTime = (ts) =>
    typeof ts === "number"
      ? new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : null;

  const renderMarkdown = (text) => {
    const lines = text.split("\n");
    return lines.map((line, i) => {
      if (line.startsWith("### ")) return <h4 key={i} style={{ margin: "8px 0 4px", fontWeight: 600, fontSize: "13px" }}>{line.slice(4)}</h4>;
      if (line.startsWith("## ")) return <h3 key={i} style={{ margin: "10px 0 4px", fontWeight: 600, fontSize: "14px" }}>{line.slice(3)}</h3>;
      if (line.startsWith("**") && line.endsWith("**")) return <p key={i} style={{ margin: "2px 0", fontWeight: 600 }}>{line.slice(2, -2)}</p>;
      if (line.startsWith("- ") || line.startsWith("• ")) return <p key={i} style={{ margin: "2px 0", paddingLeft: "12px" }}>• {line.slice(2)}</p>;
      if (line === "") return <br key={i} />;

      const parts = line.split(/(\*\*[^*]+\*\*)/g);
      return (
        <p key={i} style={{ margin: "2px 0", lineHeight: 1.6 }}>
          {parts.map((part, j) =>
            part.startsWith("**") && part.endsWith("**") ? (
              <strong key={j}>{part.slice(2, -2)}</strong>
            ) : part
          )}
        </p>
      );
    });
  };

  const S = {
    root: { display: "flex", height: "100vh", background: "#f9fafb", fontFamily: "'Segoe UI', system-ui, sans-serif", overflow: "hidden", position: "relative" },
    sidebar: { width: 280, background: "#fff", borderRight: "1px solid #e5e7eb", display: "flex", flexDirection: "column", flexShrink: 0 },
    sidebarHeader: { padding: "16px 16px 12px", borderBottom: "1px solid #f3f4f6" },
    logo: { display: "flex", alignItems: "center", gap: 8, marginBottom: 12 },
    logoIcon: { width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #166534 0%, #15803d 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 },
    logoText: { fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.2 },
    logoSub: { fontSize: 11, color: "#6b7280" },
    newChatBtn: { width: "100%", padding: "8px 12px", background: "#166534", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 8 },
    searchInput: { width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, color: "#374151", background: "#f9fafb", outline: "none", boxSizing: "border-box" },
    chatList: { flex: 1, overflowY: "auto", padding: "8px" },
    chatItem: (isActive) => ({ width: "100%", textAlign: "left", padding: "8px 10px", borderRadius: 8, border: "1px solid", borderColor: isActive ? "#86efac" : "transparent", background: isActive ? "#f0fdf4" : "transparent", cursor: "pointer", marginBottom: 2, display: "flex", alignItems: "flex-start", gap: 8 }),
    chatItemTitle: (isActive) => ({ fontSize: 13, fontWeight: 500, color: isActive ? "#166534" : "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }),
    chatItemSnippet: { fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 },
    modeTag: (modeKey) => ({ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 4, background: modeKey === "disease" ? "#fef3c7" : "#d1fae5", color: modeKey === "disease" ? "#92400e" : "#064e3b", flexShrink: 0 }),
    sidebarFooter: { padding: "12px 16px", borderTop: "1px solid #f3f4f6", fontSize: 11, color: "#9ca3af" },
    main: { flex: 1, display: "flex", flexDirection: "column", minWidth: 0 },
    topbar: { height: 56, background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", padding: "0 20px", gap: 12, flexShrink: 0 },
    topbarTitle: { flex: 1, fontSize: 15, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    modeBadge: (mode) => ({ padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: mode.bg, color: mode.color, border: `1px solid ${mode.border}`, flexShrink: 0 }),
    messagesArea: { flex: 1, overflowY: "auto", padding: "24px 20px" },
    modeSelect: { maxWidth: 560, margin: "40px auto 0" },
    modeSelectTitle: { fontSize: 22, fontWeight: 700, color: "#111827", marginBottom: 6 },
    modeSelectSub: { fontSize: 14, color: "#6b7280", marginBottom: 28 },
    modeCard: (mode) => ({ border: `1.5px solid ${mode.border}`, borderRadius: 12, padding: "20px 24px", background: "#fff", cursor: "pointer", marginBottom: 12, transition: "box-shadow .15s, transform .15s" }),
    modeCardTitle: { fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 4, display: "flex", alignItems: "center", gap: 8 },
    modeCardDesc: { fontSize: 13, color: "#6b7280", lineHeight: 1.5 },
    startersGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 20 },
    starterBtn: (mode) => ({ padding: "10px 14px", background: mode.bg, border: `1px solid ${mode.border}`, borderRadius: 8, fontSize: 12, color: mode.color, cursor: "pointer", textAlign: "left", lineHeight: 1.4 }),
    messageRow: (isUser) => ({ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", marginBottom: 16, gap: 10, alignItems: "flex-end" }),
    avatar: (isUser, mode) => ({ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, background: isUser ? "#e5e7eb" : (mode?.bg || "#d1fae5"), color: isUser ? "#374151" : (mode?.color || "#065f46"), fontWeight: 700, fontSize: 12 }),
    bubble: (isUser, mode) => ({ maxWidth: "75%", padding: "12px 16px", borderRadius: isUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px", background: isUser ? "#166534" : "#fff", color: isUser ? "#fff" : "#111827", border: isUser ? "none" : "1px solid #e5e7eb", fontSize: 13, lineHeight: 1.6 }),
    timeStamp: (isUser) => ({ fontSize: 10, color: isUser ? "rgba(255,255,255,0.65)" : "#9ca3af", marginTop: 6, textAlign: isUser ? "right" : "left" }),
    thinkingBubble: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px 18px 18px 4px", padding: "12px 16px", display: "inline-flex", gap: 4, alignItems: "center" },
    dot: (delay) => ({ width: 7, height: 7, borderRadius: "50%", background: "#9ca3af", animation: "bounce 1.2s ease-in-out infinite", animationDelay: delay }),
    composer: { background: "#fff", borderTop: "1px solid #e5e7eb", padding: "12px 20px", flexShrink: 0 },
    composerInner: { maxWidth: 760, margin: "0 auto", display: "flex", gap: 10, alignItems: "flex-end" },
    textarea: { flex: 1, padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14, color: "#111827", background: "#fff", resize: "none", outline: "none", lineHeight: 1.5, maxHeight: 140, fontFamily: "inherit" },
    sendBtn: (disabled) => ({ padding: "10px 18px", background: disabled ? "#d1fae5" : "#166534", color: disabled ? "#6b7280" : "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: disabled ? "default" : "pointer", flexShrink: 0, transition: "background .15s" }),
    composerHint: { fontSize: 11, color: "#9ca3af", marginTop: 6, textAlign: "center" },
  };

  const hasMode = !!activeChat?.mode;
  const canSend = inputMessage.trim() && !isLoading && hasMode;
  const showStarters = hasMode && messages.length === 0;

  return (
    <div style={S.root}>
      <style>{`
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-6px); }
        }
        button:hover { opacity: 0.9; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 3px; }
        textarea:focus, input:focus { border-color: #166534 !important; outline: none; }
        .mode-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
        .starter-btn:hover { opacity: 0.85; }
        .delete-btn { opacity: 0; transition: opacity 0.15s; }
        .chat-item-wrap:hover .delete-btn { opacity: 1; }
      `}</style>

      {/* Sidebar */}
      <aside style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <div style={S.logo}>
            <div style={S.logoIcon}>🌿</div>
            <div>
              <div style={S.logoText}>PoultryAI</div>
              <div style={S.logoSub}>UK Farmer Platform</div>
            </div>
          </div>
          <button style={S.newChatBtn} onClick={startNewChat}>+ New consultation</button>
          <input
            style={S.searchInput}
            placeholder="Search consultations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div style={S.chatList}>
          {filteredChats.length === 0 ? (
            <div style={{ fontSize: 13, color: "#9ca3af", padding: 8 }}>No results.</div>
          ) : (
            filteredChats.map((c) => {
              const last = c.messages?.[c.messages.length - 1];
              const snippet = (last?.content ?? "").slice(0, 50);
              const isActive = c.id === activeChatId;
              return (
                <div key={c.id} className="chat-item-wrap" style={{ position: "relative" }}>
                  <button
                    ref={isActive ? activeChatItemRef : null}
                    style={S.chatItem(isActive)}
                    onClick={() => setActiveChatId(c.id)}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        {c.mode && <span style={S.modeTag(c.mode)}>{c.mode === "disease" ? "🐔 Disease" : "♻️ Litter"}</span>}
                      </div>
                      <div style={S.chatItemTitle(isActive)}>{c.title}</div>
                      <div style={S.chatItemSnippet}>{snippet || "No messages yet"}</div>
                    </div>
                    <button
                      className="delete-btn"
                      onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this consultation?")) deleteChat(c.id); }}
                      style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "#ef4444", padding: "2px 4px", borderRadius: 4 }}
                    >
                      ✕
                    </button>
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div style={S.sidebarFooter}>
          <div style={{ marginBottom: 2 }}>🇬🇧 UK Poultry Platform</div>
          <div>Powered by AI</div>
        </div>
      </aside>

      {/* Main Content */}
      <main style={S.main}>
        <div style={S.topbar}>
          <div style={S.topbarTitle}>{activeChat?.title || "New consultation"}</div>
          {currentMode && <div style={S.modeBadge(currentMode)}>{currentMode.icon} {currentMode.label}</div>}
          {hasMode && (
            <button
              onClick={() => updateActiveChat((c) => ({ ...c, mode: null, messages: [], title: "New consultation" }))}
              style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 7, padding: "4px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer" }}
            >
              Switch module
            </button>
          )}
        </div>

        <div style={S.messagesArea}>
          {!hasMode ? (
            <div style={S.modeSelect}>
              <div style={S.modeSelectTitle}>🌿 Welcome to PoultryAI</div>
              <div style={S.modeSelectSub}>Select a module to start your AI-assisted consultation</div>
              {Object.entries(MODES).map(([key, mode]) => (
                <button key={key} className="mode-card" style={S.modeCard(mode)} onClick={() => selectMode(key)}>
                  <div style={S.modeCardTitle}>
                    <span style={{ width: 36, height: 36, borderRadius: 8, background: mode.bg, border: `1px solid ${mode.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
                      {mode.icon}
                    </span>
                    {mode.label}
                  </div>
                  <div style={S.modeCardDesc}>
                    {key === "disease" 
                      ? "List bird symptoms, match with certified poultry health professionals..." 
                      : "Get guidance on repurposing poultry litter..."}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              {showStarters && (
                <div style={{ marginBottom: 32 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 4 }}>
                    {currentMode.icon} {currentMode.label}
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
                    Ask me anything, or choose a common question to get started:
                  </div>
                  <div style={S.startersGrid}>
                    {currentMode.starters.map((s, i) => (
                      <button key={i} className="starter-btn" style={S.starterBtn(currentMode)} onClick={() => handleStarterClick(s)}>
                        "{s}"
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, idx) => {
                const isUser = m.role === "user";
                return (
                  <div key={idx} style={S.messageRow(isUser)}>
                    {!isUser && <div style={S.avatar(false, currentMode)}>AI</div>}
                    <div style={{ maxWidth: "75%" }}>
                      <div style={S.bubble(isUser, currentMode)}>
                        {isUser ? (
                          <p style={{ margin: 0, lineHeight: 1.5 }}>{m.content}</p>
                        ) : (
                          <div style={{ fontSize: 13 }}>{renderMarkdown(m.content)}</div>
                        )}
                      </div>
                      {m.ts && <div style={S.timeStamp(isUser)}>{formatTime(m.ts)}</div>}
                    </div>
                    {isUser && <div style={S.avatar(true, currentMode)}>You</div>}
                  </div>
                );
              })}

              {isLoading && (
                <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 16 }}>
                  <div style={S.avatar(false, currentMode)}>AI</div>
                  <div style={S.thinkingBubble}>
                    <div style={S.dot("0s")} />
                    <div style={S.dot("0.2s")} />
                    <div style={S.dot("0.4s")} />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {hasMode && (
          <div style={S.composer}>
            <div style={S.composerInner}>
              <textarea
                ref={textareaRef}
                rows={1}
                style={S.textarea}
                placeholder={activeChat?.mode === "disease" ? "Describe symptoms, species, flock size, age…" : "Ask about litter management, composting, W2E permits…"}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
              />
              <button style={S.sendBtn(!canSend)} onClick={() => sendMessage()} disabled={!canSend}>
                Send ↑
              </button>
            </div>
            <div style={S.composerHint}>
              Enter to send · Shift+Enter for new line · Always consult a qualified vet for diagnosis
            </div>
          </div>
        )}
      </main>
    </div>
  );
}